import { useEffect, useRef, useState } from 'react';
import { browserCameraAdapter, isCameraError, type CameraAdapter } from '../data/camera';
import type { TrainedLabel } from '../data/types';
import { browserPoseTrainer } from '../ml/browserPoseTrainer';
import { POSE_SKELETON_EDGES, detectKeypointsFromVideo } from '../ml/moveNet';
import type { ImageTrainer, LoadedModel } from '../ml/trainer';
import { ConfidenceBars } from './ConfidenceBars';
import './PredictPanel.css';

/**
 * Live prediction view for pose projects: webcam → MoveNet keypoints
 * → MLP → confidence bars. The keypoints are also drawn over the
 * preview so the kid can *see* what the model is reading. Pure webcam
 * (upload doesn't make sense for a pose-from-video classifier).
 */

const PREDICT_INTERVAL_MS = 100; // 10 Hz, matches PredictPanel

type Status = 'opening' | 'loading-model' | 'ready' | 'error';

export interface PosePredictPanelProps {
  projectId: string;
  labels: TrainedLabel[];
  onClose: () => void;
  /** Optional footer slot for the "now what?" actions — same as
   *  PredictPanel. See PredictPanel.tsx for rationale. */
  nextActions?: React.ReactNode;
  trainer?: ImageTrainer;
  camera?: CameraAdapter;
}

export function PosePredictPanel({
  projectId,
  labels,
  onClose,
  nextActions,
  trainer = browserPoseTrainer,
  camera = browserCameraAdapter,
}: PosePredictPanelProps): JSX.Element {
  const videoRef = useRef<HTMLVideoElement>(null);
  const overlayRef = useRef<HTMLCanvasElement>(null);
  const [status, setStatus] = useState<Status>('opening');
  const [error, setError] = useState<string | null>(null);
  const [probabilities, setProbabilities] = useState<number[]>(() =>
    new Array(labels.length).fill(0),
  );
  const modelRef = useRef<LoadedModel | null>(null);
  const inFlight = useRef(false);

  // Load the trained pose model once.
  useEffect(() => {
    let cancelled = false;
    (async (): Promise<void> => {
      setStatus('loading-model');
      try {
        const loaded = await trainer.loadModel(projectId);
        if (cancelled) {
          if (loaded) trainer.disposeModel(loaded);
          return;
        }
        if (!loaded) {
          setError('No trained pose model was found for this project. Train it first.');
          setStatus('error');
          return;
        }
        modelRef.current = loaded;
        setStatus('opening');
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Could not load the trained model.');
        setStatus('error');
      }
    })();
    return () => {
      cancelled = true;
      if (modelRef.current) {
        trainer.disposeModel(modelRef.current);
        modelRef.current = null;
      }
    };
  }, [projectId, trainer]);

  // Camera lifecycle.
  useEffect(() => {
    let cancelled = false;
    let stream: MediaStream | null = null;
    (async (): Promise<void> => {
      try {
        const opened = await camera.open();
        if (cancelled) {
          camera.close(opened);
          return;
        }
        stream = opened;
        if (videoRef.current) {
          videoRef.current.srcObject = opened;
          videoRef.current.muted = true;
          await videoRef.current.play().catch(() => {});
        }
        if (modelRef.current && !cancelled) setStatus('ready');
      } catch (err) {
        if (cancelled) return;
        setError(isCameraError(err) ? err.message : 'Could not turn on the camera.');
        setStatus('error');
      }
    })();
    return () => {
      cancelled = true;
      if (stream) camera.close(stream);
    };
  }, [camera]);

  // 10 Hz prediction loop.
  useEffect(() => {
    if (status !== 'ready') return;
    let stopped = false;
    const tick = async (): Promise<void> => {
      if (stopped || inFlight.current) return;
      const video = videoRef.current;
      const model = modelRef.current;
      if (!video || !model) return;
      inFlight.current = true;
      try {
        const probs = await trainer.predict(model, video);
        if (!stopped) {
          setProbabilities(probs);
          // Side-effect: paint the keypoints we *just* used over the
          // preview. We re-run detection once so the visual matches
          // exactly what the classifier saw.
          await drawKeypointsOverlay(video, overlayRef.current);
        }
      } catch {
        // swallow — next tick
      } finally {
        inFlight.current = false;
      }
    };
    const handle = setInterval(() => {
      void tick();
    }, PREDICT_INTERVAL_MS);
    void tick();
    return () => {
      stopped = true;
      clearInterval(handle);
    };
  }, [status, trainer]);

  return (
    <section className="bl-predict" aria-label="Live pose prediction">
      <header className="bl-predict__header">
        <h3 className="bl-predict__title">Test the model</h3>
        <button type="button" className="bl-predict__close" onClick={onClose}>
          Close
        </button>
      </header>
      <div className="bl-predict__viewfinder">
        <video
          ref={videoRef}
          className="bl-predict__video"
          playsInline
          muted
          aria-label="Live camera preview"
        />
        <canvas ref={overlayRef} className="bl-predict__pose-overlay" aria-hidden="true" />
        {status === 'opening' && (
          <p className="bl-predict__overlay" role="status">
            Turning on the camera…
          </p>
        )}
        {status === 'loading-model' && (
          <p className="bl-predict__overlay" role="status">
            Loading your model…
          </p>
        )}
        {status === 'error' && (
          <p className="bl-predict__overlay bl-predict__overlay--error" role="alert">
            {error}
          </p>
        )}
      </div>
      <div className="bl-predict__bars">
        <ConfidenceBars labels={labels} probabilities={probabilities} />
      </div>
      {nextActions && (
        <footer className="bl-predict__next" aria-label="What to do with this model">
          <p className="bl-predict__next-h">What&rsquo;s next</p>
          <div className="bl-predict__next-actions">{nextActions}</div>
        </footer>
      )}
    </section>
  );
}

async function drawKeypointsOverlay(
  video: HTMLVideoElement,
  canvas: HTMLCanvasElement | null,
): Promise<void> {
  if (!canvas) return;
  const w = video.clientWidth;
  const h = video.clientHeight;
  if (canvas.width !== w) canvas.width = w;
  if (canvas.height !== h) canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const keypoints = await detectKeypointsFromVideo(video).catch(() => null);
  ctx.clearRect(0, 0, w, h);
  if (!keypoints) return;

  // Same visual language as the capture overlay (cyan + halo) so test
  // mode reads like a continuation of training.
  const MIN_SCORE = 0.3;
  ctx.lineWidth = 4;
  ctx.lineCap = 'round';
  ctx.strokeStyle = 'rgba(56, 189, 248, 0.95)';
  ctx.shadowColor = 'rgba(15, 23, 42, 0.45)';
  ctx.shadowBlur = 4;
  for (const [a, b] of POSE_SKELETON_EDGES) {
    const ka = keypoints[a];
    const kb = keypoints[b];
    if (!ka || !kb) continue;
    if (ka.score < MIN_SCORE || kb.score < MIN_SCORE) continue;
    ctx.beginPath();
    ctx.moveTo(ka.x * w, ka.y * h);
    ctx.lineTo(kb.x * w, kb.y * h);
    ctx.stroke();
  }
  ctx.shadowBlur = 0;
  ctx.fillStyle = 'rgba(56, 189, 248, 0.95)';
  for (const kp of keypoints) {
    if (kp.score < MIN_SCORE) continue;
    ctx.beginPath();
    ctx.arc(kp.x * w, kp.y * h, 5, 0, Math.PI * 2);
    ctx.fill();
  }
}
